import type { BusinessFunction } from 'domain/shared/businessFunction';
import type { Technology } from 'domain/shared/career';
import { OFFICE_NAMES } from 'domain/shared/employee';
import type { OfficeType, PositionType } from 'domain/shared/employee';
import { POSITION_SLOT_API_OFFICE_LABELS } from 'domain/shared/employee/office';
import type { VacancyGroupReferencesByVacancyId } from 'domain/shared/newVacancy/useVacancyReferenceService';
import { POSITION_SLOT_WORKER_TYPE_LABELS } from './constants';
import { PositionSlot, PositionSlotListItem, PositionSlotSecondaryCompetencyItem } from './types';

export const buildPositionSlotSummaryLine = (positionSlot: PositionSlotListItem): string => {
  const workerTypeLabel = POSITION_SLOT_WORKER_TYPE_LABELS[positionSlot.workerType] || positionSlot.workerType;
  const competencyLevelSuffix = positionSlot.competencyLevel ? ` (${positionSlot.competencyLevel})` : '';

  return `${positionSlot.positionDisplayName}${competencyLevelSuffix} - ${positionSlot.officesLabel} / ${workerTypeLabel}`;
};

export const buildPositionSlotDisplayName = (specialization: string, technologyLabel: string | null) => {
  if (!technologyLabel) {
    return specialization;
  }

  return `${technologyLabel} ${specialization}`;
};

type PositionSlotEnrichmentContext = {
  positions: Pick<PositionType, 'id' | 'name'>[];
  technologies: Technology[];
  offices: Pick<OfficeType, 'id' | 'name'>[];
  businessFunctions: BusinessFunction[];
  vacancyGroupByVacancyId?: VacancyGroupReferencesByVacancyId;
};

const enrichSecondaryCompetencies = (
  positionSlot: PositionSlot,
  context: PositionSlotEnrichmentContext,
): PositionSlotSecondaryCompetencyItem[] =>
  (positionSlot.secondaryCompetencies ?? []).map(competency => ({
    ...competency,
    specializationLabel:
      context.positions.find(position => position.id === Number(competency.specializationId))?.name ||
      String(competency.specializationId),
    technologyLabel: competency.technologyId
      ? context.technologies.find(technology => technology.id === competency.technologyId)?.name ||
        competency.technologyId
      : null,
  }));

export const enrichPositionSlots = <TPositionSlot extends PositionSlot>(
  positionSlots: TPositionSlot[],
  context: PositionSlotEnrichmentContext,
): Array<TPositionSlot & PositionSlotListItem> =>
  positionSlots.map(positionSlot => enrichPositionSlot(positionSlot, context));

export const enrichPositionSlot = <TPositionSlot extends PositionSlot>(
  positionSlot: TPositionSlot,
  context: PositionSlotEnrichmentContext,
): TPositionSlot & PositionSlotListItem => {
  const officeIds = Array.isArray(positionSlot.officeIds) ? positionSlot.officeIds : [];
  const specializationLabel =
    context.positions.find(position => position.id === positionSlot.specializationId)?.name ||
    positionSlot.specialization;
  const technologyLabel = positionSlot.technologyId
    ? context.technologies.find(technology => technology.id === positionSlot.technologyId)?.name ||
      positionSlot.technology
    : null;
  const businessFunctionLabel =
    context.businessFunctions.find(businessFunction => businessFunction.id === positionSlot.businessFunctionId)?.name ||
    positionSlot.businessFunctionId;
  const vacancyGroup = positionSlot.vacancyId ? context.vacancyGroupByVacancyId?.[positionSlot.vacancyId] : undefined;
  const officesLabel = officeIds.length
    ? officeIds
        .map(officeId => {
          const officeName = context.offices.find(office => office.id === officeId)?.name;

          return (officeName && (OFFICE_NAMES[officeName] || officeName)) || String(officeId);
        })
        .join(', ')
    : (positionSlot.offices || []).map(office => POSITION_SLOT_API_OFFICE_LABELS[office] || office).join(', ');

  return {
    ...positionSlot,
    officeIds,
    specializationLabel,
    positionDisplayName: buildPositionSlotDisplayName(specializationLabel, technologyLabel),
    technologyLabel,
    secondaryCompetencyItems: enrichSecondaryCompetencies(positionSlot, context),
    businessFunctionLabel,
    officesLabel,
    vacancyGroupId: vacancyGroup?.vacancyGroupId ?? null,
    vacancyGroupName: vacancyGroup?.vacancyGroupName ?? null,
  };
};
